/** @jsxImportSource @emotion/react */
import React from "react";
import { Badge, Button as RsButton, ButtonGroup, Input } from "reactstrap";

import { SignalFilterState, SortKey } from "../signalFiltering";
import { CATEGORY_COLOR, CATEGORY_LABEL, SignalCategory, SignalCategoryList } from "../signalTypes";

interface Option {
    value: string;
    label: string;
}

interface SignalFilterRailProps {
    state: SignalFilterState;
    onChange: (patch: Partial<SignalFilterState>) => void;
    cellOptions?: Option[];
    palletOptions?: Option[];
}

export const SignalFilterRail: React.FC<SignalFilterRailProps> = ({ state, onChange, cellOptions = [], palletOptions = [] }) => {
    const toggleCategory = (category: SignalCategory) => {
        const next = state.categories.includes(category)
            ? state.categories.filter((c) => c !== category)
            : [...state.categories, category];
        onChange({ categories: next });
    };

    return (
        <div className="d-flex flex-column gap-3">
            {/* Open / Closed (Closed is read-only downstream) */}
            <ButtonGroup className="w-100">
                <RsButton color="primary" outline={state.view !== "open"} onClick={() => onChange({ view: "open" })}>
                    Open
                </RsButton>
                <RsButton color="primary" outline={state.view !== "closed"} onClick={() => onChange({ view: "closed" })}>
                    Closed
                </RsButton>
            </ButtonGroup>

            <Input
                type="search"
                placeholder="Search…"
                aria-label="Search signals"
                value={state.search}
                onChange={(e) => onChange({ search: e.target.value })}
            />

            <div>
                <div className="small text-muted mb-1">Category</div>
                <div className="d-flex flex-wrap gap-2">
                    {SignalCategoryList.map((category) => {
                        const active = state.categories.includes(category);
                        return (
                            <Badge
                                key={category}
                                role="button"
                                color={active ? CATEGORY_COLOR[category] : "light"}
                                className={active ? "" : "text-dark border"}
                                onClick={() => toggleCategory(category)}
                            >
                                {CATEGORY_LABEL[category]}
                            </Badge>
                        );
                    })}
                </div>
            </div>

            {cellOptions.length > 0 && (
                <div>
                    <div className="small text-muted mb-1">Cell</div>
                    <Input
                        type="select"
                        aria-label="Filter by cell"
                        value={state.cell || ""}
                        onChange={(e) => onChange({ cell: e.target.value || null })}
                    >
                        <option value="">All cells</option>
                        {cellOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Input>
                </div>
            )}

            {palletOptions.length > 0 && (
                <div>
                    <div className="small text-muted mb-1">Pallet</div>
                    <Input
                        type="select"
                        aria-label="Filter by pallet"
                        value={state.pallet || ""}
                        onChange={(e) => onChange({ pallet: e.target.value || null })}
                    >
                        <option value="">All pallets</option>
                        {palletOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Input>
                </div>
            )}

            <div>
                <div className="small text-muted mb-1">Sort by</div>
                <Input
                    type="select"
                    aria-label="Sort signals"
                    value={state.sort}
                    onChange={(e) => onChange({ sort: e.target.value as SortKey })}
                >
                    <option value="created">Newest</option>
                    <option value="category">Category</option>
                    <option value="status">Status</option>
                </Input>
            </div>
        </div>
    );
};

export default SignalFilterRail;
